import * as angular from 'angular';
import { SignInOption } from '~/modules/sign-in/types/sign-in-option.type';
import { getCsrfToken } from '@mode/shared/util-js';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { first } from 'rxjs/operators';
import { SignInOptionsService } from './sign-in-options.service';
import { formSubmit } from '@mode/legacy/data-access-model-js';

export const SignInOptionsComponent: angular.IComponentOptions = {
  controller: class {
    static $inject = ['SignInOptionsService', 'FeatureFlagsFacade', '$window'];
    private $resolve: any;
    private loggedOutSignInFlowEnabled: boolean;
    private redirectUrl: string | undefined;
    private authToken: string;
    public options: Array<SignInOption>;
    public defaultOption: SignInOption;
    public overflowOptions: Array<SignInOption>;
    public userEmail: string;
    public claimedDomain: boolean;
    public hasOverflowOptions: boolean;
    public showRedirectingLabel: boolean;
    public avatar;

    constructor(
      private signInOptionsService: SignInOptionsService,
      private featureFlagsFacade: FeatureFlagsFacade,
      private $window
    ) {
      this.authToken = getCsrfToken(this.$window.document);
      this.featureFlagsFacade
        .asObservable(FeatureFlag.LoggedOutSignInFlow)
        .pipe(first())
        .subscribe((enabled) => {
          this.loggedOutSignInFlowEnabled = enabled;
        });
    }

    $onInit() {
      // set properties from resolver
      if (this.$resolve) {
        this.claimedDomain = this.$resolve.signInOptionsApi.claimedDomain;
        this.options = this.$resolve.signInOptionsApi.signInOptions;
        this.avatar = this.$resolve.signInOptionsApi.avatar;
        this.userEmail = this.$resolve.authEmail;
        this.redirectUrl = this.$resolve.$stateParams.redirect_to;
      }

      this.checkForSamlRedirect();
      this.defaultOption = this.options.find((option) => option.default === true);
      this.overflowOptions = this.signInOptionsService.getAndSortOverflowOptions(this.options);
      this.hasOverflowOptions = this.overflowOptions.length > 0;
    }

    checkForSamlRedirect() {
      // if there is only one option and that option is a SAML provider
      if (this.options.length === 1 && this.options[0].saml) {
        // then redirect the user automatically to the SAML providers URL
        this.showRedirectingLabel = true;
        this.redirectUserToSamlProvider(this.options[0]);
      } else {
        this.showRedirectingLabel = false;
      }
    }

    optionIsPassword(option: SignInOption): boolean {
      return option && this.signInOptionsService.optionIsPassword(option);
    }

    redirectUserToSamlProvider(option: SignInOption) {
      formSubmit(option.url, {
        fields: {
          authenticity_token: this.authToken,
          ...(this.loggedOutSignInFlowEnabled && this.redirectUrl && { redirect_to: this.redirectUrl }),
        },
      });
    }
  },
  controllerAs: 'ctrl',
  bindings: {
    options: '<',
    claimedDomain: '<',
    userEmail: '<',
  },
  template: `
  <sign-in-title avatar="ctrl.avatar"/>
  <div class="sign-in-options-container">
    <div ng-if="ctrl.showRedirectingLabel">
      <p class="redirecting-label">
        One moment, we are redirecting you to your default sign in provider...
      </p>
    </div>

    <div ng-if="!ctrl.showRedirectingLabel">
      <p class="preferred-login" ng-if="ctrl.claimedDomain">
        Use your Workspace's preferred login:
      </p>
      <!-- START Primary Default Sign in Option -->
      <div class="sign-in-options-default-container" ng-class="{'domain-claimed': ctrl.claimedDomain,'domain-not-claimed': !ctrl.claimedDomain}">
        <sign-in-options-password-form
          ng-if="ctrl.optionIsPassword(ctrl.defaultOption)"
          default-email="ctrl.userEmail"
          option="ctrl.defaultOption"
          redirect-url="ctrl.redirectUrl"
        />
        <sign-in-sso-button
          ng-if="!ctrl.optionIsPassword(ctrl.defaultOption)"
          option="ctrl.defaultOption"
          redirect-url="ctrl.redirectUrl"
        />
      </div>
      <!-- END Primary Default Sign in Option -->

      <!-- START Secondary (Overflow) Sign In Options -->
      <div ng-if="ctrl.hasOverflowOptions" class="sign-in-options-overflow-container">
        <sign-in-toggle-options options-enabled="!ctrl.claimedDomain">
          <div ng-repeat="option in ctrl.overflowOptions" class="over-flow-option">
            <div ng-if="ctrl.optionIsPassword(option)">
              <sign-in-options-password-form
                default-email="ctrl.userEmail"
                option="option"
                redirect-url="ctrl.redirectUrl"
              />
              <hr class="password-option-hr" ng-if="ctrl.overflowOptions.length > 1"/>
            </div>
            <sign-in-sso-button
              ng-if="!ctrl.optionIsPassword(option)"
              option="option"
              redirect-url="ctrl.redirectUrl"
            />
          </div>
        </sign-in-toggle-options>
      </div>
      <!-- END Secondary (Overflow) Sign In Options -->
    </div>

  </div>
  `,
};
